/**
 * Country / language codes picker.
 *
 * Voluum identifies countries and languages by ISO codes. We keep the input
 * intentionally light (uppercase trim, max 5 chars) so it works without
 * having to thread the dictionary catalog into this editor.
 */

import { useState, type FC, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

import { Input } from '@/components/ui/form/input/Input';

export interface CountryCodesPickerProps {
  values: readonly string[];
  onChange: (next: readonly string[]) => void;
  ariaLabel: string;
  /** Tweak placeholder for languages vs countries. */
  placeholder?: string;
}

function normalise(raw: string): string {
  return raw.trim().toUpperCase().slice(0, 5);
}

const CountryCodesPicker: FC<CountryCodesPickerProps> = ({
  values,
  onChange,
  ariaLabel,
  placeholder = 'Code (e.g. US, FR, GB)',
}) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const next = normalise(draft);
    if (!next) {
      setDraft('');
      return;
    }
    if (!values.includes(next)) onChange([...values, next]);
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border/60 bg-background px-1.5 py-1">
      {values.map((code) => (
        <span
          key={code}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] tabular-nums text-foreground"
        >
          {code}
          <button
            type="button"
            aria-label={`Remove ${code}`}
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onChange(values.filter((x) => x !== code))}
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </span>
      ))}
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value.toUpperCase())}
        onBlur={commit}
        onKeyDown={onKeyDown}
        placeholder={values.length === 0 ? placeholder : ''}
        aria-label={ariaLabel}
        maxLength={5}
        className="h-7 min-w-[6rem] flex-1 border-0 bg-transparent text-xs uppercase shadow-none focus-visible:ring-0"
      />
    </div>
  );
};

export default CountryCodesPicker;
